import styled from "styled-components";
import { Row, SizedBox } from "../CommonBaseComponents";
import { C3Text } from "../text/CustomText";
import CustomIcon from "../image/CustomIcon";
import { Theme } from "../../Theme";
import { useCallback, useEffect, useState } from "react";
import CalendarComponent from "../calendar/CalendarComponent";
import { format } from "date-fns";
import { CalendarWrapper } from "./CalendarMenu";

const TitleAndInputGrid = styled.div`
    display: grid;
    grid-template-columns: 0.7fr 1.3fr;
    gap: 17px;
    width: 100%;
    box-sizing: border-box;
    align-items: ${(props) => props.alignItem || "center"};
`;

const InputContainer = styled.div`
    display: flex;
    align-items: center;
    padding: 12px 16px;
    border: ${(props) => (props.disabled ? "none" : `1px solid ${Theme.color.gray200}`)};
    border-radius: ${(props) => props.borderRadius || "8px"};
    background-color: ${(props) => (props.disabled ? Theme.color.neutral100 : "transparent")};
    width: 100%;
    box-sizing: border-box;
    position: relative;

    &:focus-within {
        border-color: #007bff;
        box-shadow: 0 0 0 1px #007bff;
    }
`;

const StyledInput = styled.input`
    flex: 1;
    border: none;
    outline: none;
    font-size: ${(props) => props.fontSize || Theme.text.c3.fontSize};
    font-family: ${(props) => props.fontFamily || Theme.text.default.fontFamily};
    letter-spacing: ${(props) => props.letterSpacing || Theme.text.default.letterSpacing};
    line-height: ${(props) => props.lineHeight || Theme.text.default.lineHeight};
    font-weight: ${(props) => props.fontWeight || Theme.text.c3.fontWeight};
    color: ${(props) => props.fontColor || Theme.text.default.fontColor};
    background-color: transparent;
    cursor: ${(props) => (!props.disabled ? "pointer" : "default")};

    &::placeholder {
        font-size: ${(props) => props.placeHolderFontSize || Theme.text.c3.fontSize};
        font-family: ${(props) => props.placeHolderFontFamily || Theme.text.default.fontFamily};
        letter-spacing: ${(props) => props.placeHolderLetterSpacing || Theme.text.default.letterSpacing};
        line-height: ${(props) => props.placeHolderLineHeight || Theme.text.default.lineHeight};
        font-weight: ${(props) => props.placeHolderFontWeight || Theme.text.c3.fontWeight};
        color: ${(props) => props.placeHolderFontColor || Theme.color.gray500};
    }
`;

function CalendarPopover({ range, setRange }) {
    return (
        <CalendarWrapper
            openedCalendarLeftSide={false}
            top={"51%"}
            $notLeftPositon={"38%"}
            backgroundColor={Theme.color.white}
            $noBlur={true}
        >
            <CalendarComponent range={range} setRange={setRange} />
        </CalendarWrapper>
    );
}

export const TitleAndCalendar = ({ title, placeholder, inputValue, setInputValue, disabled, ...props }) => {
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [range, setRange] = useState();

    const toggleCalendar = useCallback(() => {
        if (!disabled) setIsCalendarOpen((prev) => !prev);
    }, [disabled]);

    useEffect(() => {
        const start = range?.from ? format(range.from, "yyyy-MM-dd") : "2025-06-10";
        const end = range?.to ? format(range.to, "yyyy-MM-dd") : "2025-07-09";

        setInputValue(`${start} ~ ${end}`);
    }, [range, setInputValue]);

    return (
        <>
            <Row justifyContent="space-between" width="100%">
                <TitleAndInputGrid>
                    <Row alignItem="center">
                        <C3Text>{title}</C3Text>
                    </Row>

                    <InputContainer disabled={disabled}>
                        <CustomIcon
                            iconName="calendar"
                            size="12px"
                            onClick={toggleCalendar}
                            cursor={!disabled ? "pointer" : undefined}
                        />
                        <SizedBox width="10px" />

                        <StyledInput
                            type={props.inputType || "text"}
                            placeholder={placeholder}
                            value={inputValue}
                            disabled={disabled}
                            readOnly
                            onClick={toggleCalendar}
                            {...props}
                        />
                    </InputContainer>
                </TitleAndInputGrid>
            </Row>

            {isCalendarOpen && <CalendarPopover range={range} setRange={setRange} />}
        </>
    );
};
